// ============================================================================
// Exec "Where are my AMs" (handoff §3B). Read-only board for Bryan/Ben.
//   • Today tab    — each AM with their due stops (parity-aware) + RAG dots.
//   • This week tab — AM rows × Mon–Fri grid of facility chips + As-able col.
// Filters: region, RAG (attention-only), AM; facility search. No edits.
// Data: v_am_routes_today / v_am_routes_week (canonical cadence logic).
// ============================================================================

import { useMemo, useState } from 'react';
import { MapPin, RefreshCw, Search } from 'lucide-react';
import {
  ErrorState,
  PageHeader,
  RagDot,
  Skeleton,
  TabBar,
} from '../../shared/components';
import { ServiceStateChip } from '../account-management/stop-service-parts';
import {
  WEEKDAYS,
  useTodayBoard,
  useWeekRoute,
  type DayOfWeek,
  type TodayViewRow,
  type WeekViewRow,
} from './hooks/use-am-routes-data';

type ExecTab = 'today' | 'week';

const ATTENTION = new Set(['Amber', 'Red']);

export function ExecRoutes() {
  const board = useTodayBoard();
  const week = useWeekRoute(null);
  const [tab, setTab] = useState<ExecTab>('today');

  const [region, setRegion] = useState<string>('all');
  const [am, setAm] = useState<string>('all');
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [search, setSearch] = useState('');

  const regions = useMemo(() => {
    const set = new Set<string>();
    for (const r of board.rows) if (r.region) set.add(r.region);
    for (const r of week.rows) if (r.region) set.add(r.region);
    return Array.from(set).sort();
  }, [board.rows, week.rows]);

  const ams = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of board.rows) if (r.am_name) map.set(r.am_eid, r.am_name);
    for (const r of week.rows) if (r.am_name) map.set(r.am_eid, r.am_name);
    return Array.from(map.entries())
      .map(([eid, name]) => ({ eid, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [board.rows, week.rows]);

  function matches(r: TodayViewRow | WeekViewRow): boolean {
    if (region !== 'all' && r.region !== region) return false;
    if (am !== 'all' && r.am_eid !== am) return false;
    if (attentionOnly && !(r.account_status_rag && ATTENTION.has(r.account_status_rag)))
      return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const label = (r.facility_name ?? r.stop_label ?? '').toLowerCase();
      if (!label.includes(q)) return false;
    }
    return true;
  }

  const todayRows = board.rows.filter(matches);
  const weekRows = week.rows.filter(matches);
  // Paused facilities are off both surfaces (Ben, 2026-08-21). Shown as a
  // count so a filtered board never reads as a shorter one — an exec asking
  // "where is my team" must not silently lose a site.
  const pausedHidden = tab === 'today' ? board.pausedHidden : week.pausedHidden;
  const loading = tab === 'today' ? board.isLoading : week.isLoading;
  const error = tab === 'today' ? board.error : week.error;

  return (
    <div className="mx-auto max-w-7xl p-4 pb-16 sm:p-6">
      <PageHeader
        eyebrow="Routes"
        title="Where are my AMs"
        meta="Planned rotation — today and this week"
        actions={
          <button
            type="button"
            onClick={() => (tab === 'today' ? board.refetch() : week.refetch())}
            disabled={loading}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-tcs-border-strong bg-tcs-bg px-3 text-xs font-medium text-tcs-text hover:bg-tcs-bg-muted disabled:opacity-50"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        }
      />

      <div className="mt-4">
        <TabBar
          tabs={[
            { id: 'today', label: 'Today' },
            { id: 'week', label: 'This week' },
          ]}
          activeId={tab}
          onChange={(id) => setTab(id as ExecTab)}
        />
      </div>

      {/* Filters */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Select value={region} onChange={setRegion} label="Region">
          <option value="all">All regions</option>
          {regions.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </Select>
        <Select value={am} onChange={setAm} label="AM">
          <option value="all">All AMs</option>
          {ams.map((a) => (
            <option key={a.eid} value={a.eid}>
              {a.name}
            </option>
          ))}
        </Select>
        {pausedHidden > 0 && (
          <span
            className="inline-flex items-center rounded-lg border border-st-info-border bg-st-info-bg px-3 py-2 text-xs font-semibold text-st-info-fg-strong"
            title="Service is on hold at these facilities, so their stops are off the route. The rotation is untouched and they return when service resumes."
          >
            {pausedHidden} paused — not shown
          </span>
        )}
        <label className="inline-flex items-center gap-1.5 rounded-lg border border-tcs-border bg-tcs-bg px-3 py-2 text-xs font-medium text-tcs-text">
          <input
            type="checkbox"
            checked={attentionOnly}
            onChange={(e) => setAttentionOnly(e.target.checked)}
            className="h-3.5 w-3.5 accent-rag-red"
          />
          Amber / Red only
        </label>
        <div className="relative min-w-[180px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tcs-text-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search facility…"
            className="w-full rounded-lg border border-tcs-border bg-tcs-field py-2 pl-9 pr-3 text-sm focus:border-tcs-accent focus:outline-none"
          />
        </div>
      </div>

      {error && (
        <div className="mt-4">
          <ErrorState body={error} />
        </div>
      )}

      {loading && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} block height={120} className="w-full rounded-xl" />
          ))}
        </div>
      )}

      {!loading && !error && tab === 'today' && (
        <TodayBoard rows={todayRows} />
      )}
      {!loading && !error && tab === 'week' && <WeekGrid rows={weekRows} />}
    </div>
  );
}

// ----- Today board: one card per AM ----------------------------------------

function TodayBoard({ rows }: { rows: TodayViewRow[] }) {
  const byAm = useMemo(() => {
    const map = new Map<
      string,
      { name: string; parity: string | null; stops: TodayViewRow[] }
    >();
    for (const r of rows) {
      const key = r.am_eid;
      if (!map.has(key))
        map.set(key, {
          name: r.am_name ?? r.am_eid,
          parity: r.current_parity,
          stops: [],
        });
      map.get(key)!.stops.push(r);
    }
    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [rows]);

  if (byAm.length === 0) {
    return (
      <div className="mt-6 flex flex-col items-center rounded-xl border border-dashed border-tcs-border-strong bg-tcs-bg p-10 text-center text-tcs-text-muted">
        <MapPin className="h-10 w-10" strokeWidth={1.5} />
        <p className="mt-3 text-sm">No AMs have stops matching these filters today.</p>
      </div>
    );
  }

  return (
    <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {byAm.map((amCard) => (
        <div
          key={amCard.name}
          className="rounded-xl border border-tcs-border bg-tcs-raised p-4 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-tcs-text">{amCard.name}</h3>
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-tcs-text-secondary">
              Week
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-tcs-inverse text-[11px] font-bold text-tcs-inverse-ink">
                {amCard.parity ?? '—'}
              </span>
            </span>
          </div>
          <p className="mt-0.5 text-[11px] text-tcs-text-muted">
            {amCard.stops.length} stop{amCard.stops.length === 1 ? '' : 's'}
          </p>
          <ul className="mt-2.5 space-y-1.5">
            {amCard.stops.map((s) => (
              <li key={s.assignment_id} className="flex items-center gap-2 text-sm">
                <RagDot status={s.account_status_rag} />
                <span className="min-w-0 flex-1 truncate text-tcs-text">
                  {s.facility_name ?? s.stop_label}
                </span>
                <ServiceStateChip state={s.service_state} size="sm" />
                {s.coverage === 'assist' && (
                  <span className="shrink-0 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">
                    Helping {(s.owner_name ?? '').split(' ')[0] || 'AM'}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// ----- Week grid: AM rows × Mon–Fri + As-able -------------------------------

const GRID_DAYS: DayOfWeek[] = WEEKDAYS.slice(0, 5);

function WeekGrid({ rows }: { rows: WeekViewRow[] }) {
  const ams = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rows) map.set(r.am_eid, r.am_name ?? r.am_eid);
    return Array.from(map.entries())
      .map(([eid, name]) => ({ eid, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const cell = (eid: string, day: DayOfWeek | 'asable') =>
    rows.filter((r) =>
      r.am_eid === eid &&
      (day === 'asable' ? r.is_as_able : !r.is_as_able && r.day_of_week === day),
    );

  if (ams.length === 0) {
    return (
      <div className="mt-6 rounded-xl border border-dashed border-tcs-border-strong bg-tcs-bg p-10 text-center text-sm text-tcs-text-muted">
        No AMs match these filters this week.
      </div>
    );
  }

  return (
    <div className="mt-4 overflow-x-auto rounded-xl border border-tcs-border">
      <table className="w-full min-w-[760px] border-collapse text-sm">
        <thead>
          <tr className="bg-tcs-bg">
            <th className="sticky left-0 z-10 bg-tcs-bg px-3 py-2 text-left text-xs font-semibold text-tcs-text-secondary">
              AM
            </th>
            {GRID_DAYS.map((d) => (
              <th
                key={d}
                className="px-3 py-2 text-left text-xs font-semibold text-tcs-text-secondary"
              >
                {d.slice(0, 3)}
              </th>
            ))}
            <th className="px-3 py-2 text-left text-xs font-semibold text-tcs-text-secondary">
              As able
            </th>
          </tr>
        </thead>
        <tbody>
          {ams.map((a) => (
            <tr key={a.eid} className="border-t border-tcs-border align-top">
              <td className="sticky left-0 z-10 bg-tcs-raised px-3 py-2 font-medium text-tcs-text">
                {a.name}
              </td>
              {GRID_DAYS.map((d) => (
                <td key={d} className="px-2 py-2">
                  <CellChips stops={cell(a.eid, d)} />
                </td>
              ))}
              <td className="px-2 py-2">
                <CellChips stops={cell(a.eid, 'asable')} muted />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CellChips({ stops, muted }: { stops: WeekViewRow[]; muted?: boolean }) {
  if (stops.length === 0)
    return <span className="text-xs text-tcs-text-muted/50">—</span>;
  return (
    <div className="flex flex-col gap-1">
      {stops.map((s) => (
        <span
          key={s.assignment_id}
          className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] ${
            s.coverage === 'assist'
              ? 'bg-violet-50 text-violet-800 ring-1 ring-inset ring-violet-200'
              : muted
                ? 'bg-tcs-bg text-tcs-text-secondary'
                : 'bg-tcs-surface text-tcs-text'
          }`}
          title={
            s.coverage === 'assist' && s.owner_name
              ? `Helping ${s.owner_name}`
              : undefined
          }
        >
          <RagDot status={s.account_status_rag} />
          {/* A dot, not a pill: this cell already packs several stops into one
              column and a word would push the facility name out of view. */}
          <ServiceStateChip state={s.service_state} size="dot" />
          <span className="max-w-[140px] truncate">
            {s.facility_name ?? s.stop_label}
          </span>
        </span>
      ))}
    </div>
  );
}

function Select({
  value,
  onChange,
  label,
  children,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-lg border border-tcs-border bg-tcs-bg px-2.5 text-xs font-medium text-tcs-text focus:border-tcs-accent focus:outline-none"
    >
      {children}
    </select>
  );
}
